import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  notificationSettingFilter,
  categoryNames,
  setQuote,
} from 'pages/NotificationList';

import { formatDateForString } from 'utils/formatter';

import * as api from 'config/api';

import * as env from 'config/environment';
import { useRouting } from 'hooks/useRouting';
import { initializeScrolTop } from 'utils/dom';
import * as consts from 'config/consts';
import Loading from 'components/commons/loading/Loading';

function autoLink(content: string): string {
  const newContent = unescape(content ?? '');

  // コメントの部分は、正規表現（/〜/）を解釈してくれないエディタ等で自動整形を崩さないため
  // url[text] -> ]url: リンクURL、text: リンク文字列
  const regexpUrl = /(?<url>https?:\/\/[-_.!~*'()a-zA-Z0-9;/?:@&=+$,%#]+)(\[:b (?<text>.*)\]){0,1}/g; // ']))/;

  return newContent.replace(regexpUrl, (...args) => {
    const groups = args.pop();
    const url = groups['url'];
    const text = groups['text'];

    // 画像は特定の拡張子のみ画像表示されるようにする
    if (
      url.indexOf('.jpg') !== -1 ||
      url.indexOf('.jpeg') !== -1 ||
      url.indexOf('.png') !== -1 ||
      url.indexOf('.gif') !== -1 ||
      url.indexOf('.svg') !== -1
    ) {
      return `<img src="${url}" alt="お知らせ画像" class="mx-auto" />`;
    }

    // pdfへのリンクの場合、コンテンツ内でPDFViewerを表示する
    if (url.indexOf('.pdf') !== -1) {
      const viewer = `https://drive.google.com/viewerng/viewer?embedded=true&url=${encodeURI(
        url
      )}`;
      return `
        <div class="flex flex-row items-center text-link whitespace-normal">
          <div class="">
            <a
              href=${encodeURI(url)}
              target="_blank"
              rel="noopener noreferrer">
              PDFの内容が表示されない方はこちら
            </a>
          </div>
          <div class="icon-external-link ml-1 text-lg"></div>
        </div><div
          class="w-full pb-4 whitespace-normal"
          style="height: 500px">
          <iframe
            key=${url}
            title="pdf"
            src=${viewer}
            class="w-full h-full"></iframe>
        </div>`;
    }

    // アプリケーション内へのリンク -> 同じタブ、それ以外 -> 別タブ で開く
    const target = url.startsWith(env.appURL) ? '' : 'target="_blank"';
    if (text) {
      return `<div class="ui-button theme-outline my-4 mx-4 p-2"><a class="text-link" href="${url}" ${target}>${text}</a></div>`;
    } else {
      return `<a class=${'text-link'} href="${url}" ${target}>${url}</a>`;
    }
  });
}

function createContent(
  content: string | undefined
): {
  __html: string;
} {
  return { __html: autoLink(unescape(content ?? '')) };
}

const NotificationDetail: React.FC = () => {
  // URLパラメータ情報
  const { menuNo, notificationID, symbol, func } = useParams<{
    // サブメニューの選択タブ
    menuNo: string;
    // お知らせID
    notificationID: string;
    // 銘柄コード
    symbol?: string;
    // お知らせ種別
    func?: string;
  }>();
  // お知らせ一覧情報
  const [notifications, setNotifications] = useState<
    api.GetNotificationResult | undefined
  >(undefined);
  // お知らせ明細
  const [notificationDetail, setNotificationDetail] = useState<
    api.GetNotificationItem | undefined
  >(undefined);

  const routing = useRouting();
  const unmounted = useRef<boolean>(false);

  const goBack = (): void => {
    routing.backRouting(true);
  };

  const chkTarget = (shiftNum: number): number => {
    const invalid = -1;
    if (!notifications || !notificationDetail) return invalid;

    let targetIndex = notifications.Items.findIndex(
      item => item.ID === notificationDetail?.ID
    );
    if (targetIndex === invalid) return invalid;

    // シフトさせた位置にレコードが存在すれば、その位置を返却
    targetIndex += shiftNum;
    if (targetIndex >= 0 && targetIndex <= notifications.Items.length - 1) {
      return targetIndex;
    }
    return invalid;
  };

  const getNotificationDetail = (shiftNum: number): void => {
    initializeScrolTop();
    if (!notifications || !notificationDetail) return;

    const targetIndex = chkTarget(shiftNum);
    if (targetIndex === -1) return;

    // 表示対象IDを参照
    const newReadID = notifications.Items[targetIndex].ID;

    // 既読更新を呼び出す
    const IDList: string[] = [setQuote(newReadID)];
    api.setReadNotification(IDList).then(ret => {
      if (!unmounted.current && ret.Success) {
        // 既読状態に更新して表示
        const newDetail = Object.assign(
          {},
          notifications.Items.find(item => item.ID === newReadID)
        );
        setNotificationDetail({ ...newDetail, ReadStatus: true });
      }
    });
  };

  useEffect(() => {
    return () => {
      // unmount
      unmounted.current = true;
    };
  }, []);

  useEffect(() => {
    const readID = String(notificationID);

    // お知らせ一覧の取得
    api.getNotifications(symbol, func).then(ret => {
      if (!unmounted.current && ret) {
        // お知らせ設定の内容で絞り込む
        const dataFilter = notificationSettingFilter(ret, menuNo);
        // 対象のお知らせ一覧を保持
        setNotifications(dataFilter);
        // お知らせ明細を設定
        setNotificationDetail(
          dataFilter.Items.find(item => item.ID === readID)
        );
      }
    });

    if (func !== 'DIVIDEND') {
      // 既読更新を呼び出す
      const IDList: string[] = [setQuote(readID)];
      api.setReadNotification(IDList).then(ret => {
        if (!unmounted.current && ret.Success) {
        }
      });
    }
  }, [notificationID, menuNo]);

  if (notificationDetail === undefined || notificationDetail === null)
    return (
      <Loading
        loading={true}
        style={{
          backgroundColor: '#edf2f7',
          alignItems: 'normal',
          paddingTop: '200px',
          top: '95',
          left: '0',
        }}
      />
    );

  const title =
    func === 'DIVIDEND'
      ? notificationDetail.Title.replace(/^【.*\)】/g, '')
      : notificationDetail.Title;

  return (
    <div>
      <div></div>
      <div className="bg-white rounded m-3 p-3">
        <div className="flex text-gray-500 text-sm p-2">
          <div className="">
            {formatDateForString(
              notificationDetail.InsTime,
              'yyyy/MM/dd HH:mm'
            )}
          </div>
          <div className="ml-6">
            {notificationDetail && categoryNames[notificationDetail.Type]}
          </div>
        </div>
        <div className="break-all p-2">
          <div className="font-bold">{title}</div>
          <div
            className="mt-4 whitespace-pre-wrap"
            id="contentWithAutoLink"
            dangerouslySetInnerHTML={createContent(notificationDetail.Content)}
          />
        </div>

        {func !== 'DIVIDEND' ? (
          <>
            {notificationDetail.Type ===
            consts.notificationTypes.重要なお知らせ ? (
              <>
                <div
                  className="flex justify-center text-xl text-gray-500 p-2 cursor-pointer"
                  onClick={goBack}>
                  戻る
                </div>
              </>
            ) : (
              <>
                <div className="flex p-2">
                  <div
                    className={`ui-prev-next ${
                      chkTarget(-1) === -1
                        ? 'ui-prev-next-disabled'
                        : 'cursor-pointer'
                    }`}
                    onClick={(): void => getNotificationDetail(-1)}>
                    前
                  </div>
                  <div className="flex-grow"></div>
                  <div
                    className={`ui-prev-next ${
                      chkTarget(1) === -1
                        ? 'ui-prev-next-disabled'
                        : 'cursor-pointer'
                    }`}
                    onClick={(): void => getNotificationDetail(1)}>
                    次
                  </div>
                </div>
                <div className="ui-border-b pt-2" />
                <div className="p-2">
                  <div className="text-link cursor-pointer" onClick={goBack}>
                    お知らせ一覧へ戻る
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

export default NotificationDetail;
